import * as THREE from 'three';
import { ModelLoader, modelLoader } from './modelLoader';
import tunnelAlgorithm from './tunnelAlgorithm';
import './types'; // Import global types
import { MeshPhysicalMaterial } from 'three';

// Define interfaces for the model context
interface TileContext {
    north: number | null;
    northeast: number | null;
    east: number | null;
    southeast: number | null;
    south: number | null;
    southwest: number | null;
    west: number | null;
    northwest: number | null;
    center: number | null;
}

/**
 * TileRenderer class - TypeScript version
 * Handles the rendering of tiles using GLTF models
 */

class TileRenderer {
    // Flag to check if initialization is complete
    public initialized: boolean = false;
    private WALL_OFFSET: number = 0.45;
    private CORNER_OFFSET_X: number = 0.5;
    private CORNER_OFFSET_Z: number = 0.25;
    private TORCH_OFFSET: number = 0.5;
    private TORCH_PROBABILITY: number = 0.04;
    public TORCH_LIGHT_COLOR: number = 0xFFF3DA;
    public TORCH_LIGHT_INTENSITY: number = 3;
    private TORCH_LIGHT_DISTANCE: number = 20;

    // Light render options
    public LIGHT_SHADOW_MAP_SIZE: number = 512;
    public LIGHT_SHADOW_RADIUS: number = 8;

    // Floor tile variation
    private FLOOR_BASE_MODEL: string = "Dungeon_ground_tile1_DungeonProps_0";
    private FLOOR_TILE_VARIATION_1 = {probability: 0.1, model: "Dungeon_ground_tile2_DungeonProps_0"};
    private FLOOR_TILE_VARIATION_2 = {probability: 0.2, model: "Dungeon_ground_tile3_DungeonProps_0"};
    private FLOOR_TILE_VARIATION_3 = {probability: 0.3, model: "Dungeon_ground_tile4_DungeonProps_0"};
    
    // Reference to the model loader
    private modelLoader: ModelLoader | null = null;

    constructor() {}

    /**
     * Initialize the TileRenderer
     */
    init(): void {
        // Get a reference to the singleton modelLoader?
        this.modelLoader = modelLoader;

        // Flag initialization as complete
        this.initialized = true;
        //console.log("TileRenderer initialized");
    }

    /**
     * Hash function to convert x,z coordinates to a value between 0-1
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate
     * @param {number} frequency - Frequency of pattern
     * @returns {number} - Deterministic random value between 0-1
     */
    hash(x: number, z: number, frequency: number = 5): number {
        // Scale coordinates by frequency and convert to integers
        const ix = Math.floor(x * frequency);
        const iz = Math.floor(z * frequency);
        
        const MIX_FACTOR = 31; // Mix factor for coordinates
        let freq = frequency;
        freq ^= ix * MIX_FACTOR;
        freq ^= iz * 65537; // Using a prime number for z mixing
        
        // Apply the Mulberry32 algorithm
        freq |= 0; 
        freq = freq + 0x6D2B79F5 | 0; // Mulberry32 constant
        
        // Core Mulberry32 algorithm
        let t = Math.imul(freq ^ freq >>> 15, 1 | freq);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        
        // Convert to 0-1 range with full precision
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    }

    /**
     * Create a tile mesh using GLTF models if available
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate
     * @param {number} tileType - Type of tile (0=floor, 1=wall)
     * @param {object} geometries - Fallback geometries
     * @param {object} materials - Materials
     * @param {number} TILE_SIZE - Size of tiles
     * @returns {THREE.Mesh} - The created mesh
     */
    createTileMesh(
        x: number, 
        z: number, 
        tileType: number, 
        geometries: Record<string, THREE.BufferGeometry>, 
        materials: Record<string, THREE.Material>, 
        TILE_SIZE: number,
        forceTorch: boolean = false
    ): THREE.Object3D | null {
        // Get context information about surrounding tiles
        const context = this.getTileContext(x, z);
        
        if (tileType === 0) { // Floor
            // Create a group to hold the floor tile and any adjacent wall models
            const tileGroup = new THREE.Group();
            tileGroup.position.set(x, 0, z);

            const hashValue = this.hash(x, z, 10);
            const floorVariation = this.getFloorVariation(hashValue);
            
            // Add the base floor model
            const floorModel = this.modelLoader.getModelByName(floorVariation) as THREE.Object3D;

            if (floorModel) {
                this.rotateRandom(floorModel, Math.floor(Math.random() * 4));
                
                // Reset position to local coordinates since the group is positioned
                floorModel.position.set(0, 0, 0);

                // Ensure the model receives shadows
                floorModel.traverse(child => {
                    if (child instanceof THREE.Mesh) {
                        child.receiveShadow = true;
                        child.castShadow = true;
                    }
                });

                // Add to the group
                tileGroup.add(floorModel);

                // Determine which adjacent tiles are walls and need wall models
                const wallSides: string[] = [];
                if (context.north === 1) wallSides.push('north');
                if (context.east === 1) wallSides.push('east');
                if (context.south === 1) wallSides.push('south');
                if (context.west === 1) wallSides.push('west');

                // For each adjacent wall, add a wall model
                let torchAdded = false;
                wallSides.forEach((side) => {
                    const wallModel = this.modelLoader.getModelByName("DungeonWall2_DungeonProps_0");

                    if (wallModel) {
                        wallModel.scale.set(
                            wallModel.scale.x * 1.1, 
                            wallModel.scale.y * 1.1, 
                            wallModel.scale.z * 1.494
                        );
                        
                        // Reposition
                        wallModel.position.set(wallModel.position.x, 10, wallModel.position.z);
                        
                        // Position at edge based on direction
                        if (side === 'north') {
                            wallModel.position.set(0, 0, -this.WALL_OFFSET);
                        } else if (side === 'east') {
                            wallModel.position.set(this.WALL_OFFSET, 0, 0);
                        } else if (side === 'south') {
                            wallModel.position.set(0, 0, this.WALL_OFFSET);
                        } else if (side === 'west') {
                            wallModel.position.set(-this.WALL_OFFSET, 0, 0);
                        }

                        const shouldPlaceTorch = forceTorch || hashValue <= this.TORCH_PROBABILITY;

                        if (shouldPlaceTorch && !torchAdded) {
                            // Add a torch  
                            const torchModel = this.modelLoader?.getModelByName("Torch001_Texture_0");
                            
                            if (torchModel) {
                                const torchScale = torchModel.scale;
                                const scaleFactor = 0.5;
                                torchModel.scale.set(
                                    torchScale.x * scaleFactor, 
                                    torchScale.y * scaleFactor, 
                                    torchScale.z * scaleFactor
                                );
                                
                                const torchOffsetY = 0.85;
                                if (side === 'north') {
                                    torchModel.position.set(0, torchOffsetY, -this.TORCH_OFFSET);
                                } else if (side === 'east') {
                                    torchModel.position.set(this.TORCH_OFFSET, torchOffsetY, 0);
                                } else if (side === 'south') {
                                    torchModel.position.set(0, torchOffsetY, this.TORCH_OFFSET);
                                } else if (side === 'west') {
                                    torchModel.position.set(-this.TORCH_OFFSET, torchOffsetY, 0);
                                }
                                
                                this.rotateTorchForDirection(torchModel, side);
                                this.setupShadowsForModel(torchModel);
                                tileGroup.add(torchModel);

                            // Add torch light placeholder instead of actual light
                            const torchPlaceholder = new THREE.Object3D();
                            torchPlaceholder.userData = {
                                isTorchPlaceholder: true,
                                color: this.TORCH_LIGHT_COLOR,
                                intensity: this.TORCH_LIGHT_INTENSITY,
                                distance: this.TORCH_LIGHT_DISTANCE,
                                side: side // This is used to identify the direction of the spotlight
                            };
                            
                            // Position the placeholder
                            this.calculateTorchLightPosition(torchPlaceholder, torchOffsetY * 2.5, side);
                            
                            tileGroup.add(torchPlaceholder);
                            torchAdded = true;

                                // Add torch flame
                                const torchFlame = this.modelLoader?.getModelByName("Fire_LPolyPalete_0");
                                
                                if (torchFlame) {
                                    const torchFlameScale = torchFlame.scale;
                                    const torchFlameScaleFactor = 0.25;
                                    torchFlame.scale.set(
                                        torchFlameScale.x * torchFlameScaleFactor, 
                                        torchFlameScale.y * torchFlameScaleFactor, 
                                        torchFlameScale.z * torchFlameScaleFactor
                                    );
                                    
                                    torchFlame.userData = {
                                        flickerPhase: Math.random() * Math.PI * 2,
                                    };
                                    
                                    torchFlame.position.set(
                                        torchPlaceholder.position.x, 
                                        torchPlaceholder.position.y-0.7, 
                                        torchPlaceholder.position.z
                                    );
                                    
                                    if (torchFlame instanceof THREE.Mesh) {
                                        torchFlame.material = new THREE.MeshStandardMaterial({
                                            color: 0xffa500, // Warm orange color
                                            emissive: 0xFFA500, // Glowing effect with a warm white glow
                                            emissiveIntensity: 0.5, // Adjust intensity for glow effect
                                            transparent: true, // Enable transparency
                                            opacity: 0.4, // Adjust opacity
                                            depthWrite: false, // Prevents depth issues with transparency
                                            blending: THREE.AdditiveBlending, // Makes it glow
                                        });
                                    }
                                    
                                    tileGroup.add(torchFlame);
                                }
                            }
                        }

                        // Rotate to face the floor tile
                        this.rotateWallForDirection(wallModel, side);

                        // Set up the wall model and add to group
                        this.setupShadowsForModel(wallModel);

                        // Add a userData property to identify this as a wall tile
                        wallModel.userData = {
                            isCollidable: true
                        };
                        
                        tileGroup.add(wallModel);
                    }
                });

                // Check for pillars
                this.addPillars(tileGroup, context, 'floor');

                // Add a userData property to identify this as a floor tile
                tileGroup.userData = {
                    tileType: 'floor',
                    isCollidable: false,
                    isGLTF: true,
                    context: context
                };

                return tileGroup;
            }
            else {
                console.log("No floor model found for tile at", x, z);
                // Fallback to original floor mesh
                return this.createFallbackMesh(x, z, tileType, geometries, materials, TILE_SIZE);
            }
        }
        else if (tileType === 1) { // Wall
            // For wall tiles, just use the default mesh
            return this.createFallbackMesh(x, z, tileType, geometries, materials, TILE_SIZE);
        }

        // Fallback for any other tile type
        return this.createFallbackMesh(x, z, tileType, geometries, materials, TILE_SIZE);
    }

    /**
     * Get the appropriate floor variation model name based on hash value
     * @param hashValue - Random hash value for this position
     * @returns The model name to use
     */
    getFloorVariation(hashValue: number): string {
        if (hashValue <= this.FLOOR_TILE_VARIATION_1.probability) {
            return this.FLOOR_TILE_VARIATION_1.model;
        }
        else if (hashValue <= this.FLOOR_TILE_VARIATION_2.probability) {
            return this.FLOOR_TILE_VARIATION_2.model;
        }
        else if (hashValue <= this.FLOOR_TILE_VARIATION_3.probability) {
            return this.FLOOR_TILE_VARIATION_3.model;
        }
        else {
            return this.FLOOR_BASE_MODEL;
        }
    }

    /**
     * Rotate a wall model to face the specified direction
     * @param {THREE.Object3D} wallModel - The wall model to rotate
     * @param {string} direction - The direction to face ('north', 'east', 'south', 'west')
     */
    rotateWallForDirection(wallModel: THREE.Object3D, direction: string): void {
        // Handle cardinal directions
        if (direction === 'north') {
            wallModel.rotation.z = 0;
        } else if (direction === 'east') {
            wallModel.rotation.z = -Math.PI / 2;
        } else if (direction === 'south') {
            wallModel.rotation.z = Math.PI;
        } else if (direction === 'west') {
            wallModel.rotation.z = Math.PI / 2;
        }
        wallModel.updateMatrixWorld(true);
    }

    /**
     * Rotate model randomly
     * @param model - The model to rotate
     * @param number - Rotation index (0-3)
     */
    rotateRandom(model: THREE.Object3D, number: number): void {
        switch (number) {
            case 0:
                model.rotation.z = 0;
                break;
            case 1:
                model.rotation.z = -Math.PI / 2;
                break;
            case 2:
                model.rotation.z = Math.PI;
                break;
            case 3:
                model.rotation.z = Math.PI / 2;
                break;
        }
        model.updateMatrixWorld(true);
    }

    /**
     * Calculate torch light position
     * @param torchModel - The torch light placeholder
     * @param yOffset - Height offset
     * @param direction - Direction the torch faces
     */
    calculateTorchLightPosition(torchModel: THREE.Object3D, yOffset: number, direction: string): void {
        // Handle cardinal directions
        const adjustment = 0.4;
        if (direction === 'north') {
            torchModel.position.set(0, yOffset, -this.TORCH_OFFSET + adjustment);
        } else if (direction === 'east') {
            torchModel.position.set(this.TORCH_OFFSET - adjustment, yOffset, 0);
        } else if (direction === 'south') {
            torchModel.position.set(0, yOffset, this.TORCH_OFFSET - adjustment);
        } else if (direction === 'west') {
            torchModel.position.set(-this.TORCH_OFFSET + adjustment, yOffset, 0);
        }
    }

    /**
     * Rotate a torch model to face the specified direction
     * @param {THREE.Object3D} torchModel - The torch model to rotate
     * @param {string} direction - The direction to face ('north', 'east', 'south', 'west')
     */
    rotateTorchForDirection(torchModel: THREE.Object3D, direction: string): void {
        // Handle cardinal directions
        if (direction === 'north') {
            torchModel.rotation.z = -Math.PI / 2;
        } else if (direction === 'east') {
            torchModel.rotation.z = Math.PI;
        } else if (direction === 'south') {
            torchModel.rotation.z = Math.PI / 2;
        } else if (direction === 'west') {
            torchModel.rotation.z = 0;
        }
    }

    /**
     * Set up common properties for models
     * @param {THREE.Object3D} model - The model to set up
     */
    setupShadowsForModel(model: THREE.Object3D): void {
        model.traverse(child => {
            if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });
    }

    /**
     * Create a fallback mesh when no suitable model is available
     */
    createFallbackMesh(
        x: number, 
        z: number, 
        tileType: number, 
        geometries: Record<string, THREE.BufferGeometry>, 
        materials: Record<string, THREE.Material>, 
        TILE_SIZE: number
    ): THREE.Mesh {
        if (tileType === 0) { // Floor
            const floorMesh = new THREE.Mesh(geometries.floor, materials.floor);
            floorMesh.position.set(x, 0, z);
            floorMesh.receiveShadow = false;
            floorMesh.userData = { tileType: 'floor', isCollidable: false };
            return floorMesh;
        } else if (tileType === 1) { // Wall
            const wallMesh = new THREE.Mesh(geometries.wall, materials.wall);
            wallMesh.position.set(x, TILE_SIZE, z);
            wallMesh.castShadow = false;
            wallMesh.receiveShadow = false;
            wallMesh.userData = { tileType: 'wall', isCollidable: true };
            return wallMesh;
        }
        
        // Default fallback for unknown tile types
        const defaultMesh = new THREE.Mesh(geometries.floor, materials.floor);
        defaultMesh.position.set(x, 0, z);
        return defaultMesh;
    }

    /**
     * Get context information about a tile's surroundings
     * @param {number} x - X coordinate of the tile
     * @param {number} z - Z coordinate of the tile
     * @returns {object} - Context information about surrounding tiles
     */
    getTileContext(x: number, z: number): TileContext {
        // Use TunnelAlgorithm to get surrounding tiles
        const tilesInRange = tunnelAlgorithm.getTilesToRender(x, z, 2, 2);

        // Check for tiles in all 8 directions
        const context: TileContext = {
            north: tilesInRange.get(`${x},${z - 1}`), // Default to wall (1) if unknown
            northeast: tilesInRange.get(`${x + 1},${z - 1}`),
            east: tilesInRange.get(`${x + 1},${z}`),
            southeast: tilesInRange.get(`${x + 1},${z + 1}`),
            south: tilesInRange.get(`${x},${z + 1}`),
            southwest: tilesInRange.get(`${x - 1},${z + 1}`),
            west: tilesInRange.get(`${x - 1},${z}`),
            northwest: tilesInRange.get(`${x - 1},${z - 1}`),
            center: tilesInRange.get(`${x},${z}`)
        };

        return context;
    }

    /**
     * Adds corner wall pieces where appropriate
     * @param {THREE.Group} tileGroup - Group to add corner walls to
     * @param {Object} context - Context of surrounding tiles
     * @param {string} tileType - Type of the center tile
     */
    addPillars(tileGroup: THREE.Group, context: TileContext, tileType: string): void {
        // Only add corner walls to floor tiles
        if (tileType !== 'floor') return;

        // Check northeast corner - need floor at north and east, wall at northeast
        if (context.north === 0 && context.east === 0 &&
            context.northeast === 1) {
            this.addPillar(tileGroup, 'northeast');
        }

        // Check northwest corner - need floor at north and west, wall at northwest
        if (context.north === 0 && context.west === 0 &&
            context.northwest === 1) {
            this.addPillar(tileGroup, 'northwest');
        }

        // Check southeast corner - need floor at south and east, wall at southeast
        if (context.south === 0 && context.east === 0 &&
            context.southeast === 1) {
            this.addPillar(tileGroup, 'southeast');
        }

        // Check southwest corner - need floor at south and west, wall at southwest
        if (context.south === 0 && context.west === 0 &&
            context.southwest === 1) {
            this.addPillar(tileGroup, 'southwest');
        }
    }

    /**
     * Add a corner wall model to a tile group
     * @param {THREE.Group} tileGroup - Group to add the corner wall to
     * @param {string} corner - Which corner to add the wall to ('northeast', 'northwest', 'southeast', 'southwest')
     */
    addPillar(tileGroup: THREE.Group, corner: string): void {
        if (!this.modelLoader) return;
        
        const cornerWallModel = this.modelLoader.getModelByName("DungeonPillar_DungeonProps_0")?.clone();

        if (!cornerWallModel) return;

        // scale model 
        const scale = cornerWallModel.scale;
        const scaleModifier = 0.42; // Specific for this model
        cornerWallModel.scale.set(
            scale.x * scaleModifier, 
            scale.y * scaleModifier * 1.3, 
            scale.z * scaleModifier
        );

        // Set position based on the corner - specific for this model
        if (corner === 'northeast') {
            cornerWallModel.position.set(this.CORNER_OFFSET_X, 0, -this.CORNER_OFFSET_Z);
        } else if (corner === 'northwest') {
            cornerWallModel.position.set(-this.CORNER_OFFSET_X, 0, -this.CORNER_OFFSET_Z);
        } else if (corner === 'southeast') {
            cornerWallModel.position.set(this.CORNER_OFFSET_X, 0, this.CORNER_OFFSET_Z * 2.8);
        } else if (corner === 'southwest') {
            cornerWallModel.position.set(-this.CORNER_OFFSET_X, 0, this.CORNER_OFFSET_Z * 2.8);
        }

        // Set up the wall model (shadows, etc.)
        this.setupShadowsForModel(cornerWallModel);

        // Add metadata for identification
        cornerWallModel.userData = { 
            type: 'cornerWall', 
            isCollidable: true, 
            corner: corner 
        };
        
        cornerWallModel.updateMatrixWorld(true);
        
        // Add the corner wall to the tile group
        tileGroup.add(cornerWallModel);
    }
}

// Create global instance
const tileRenderer = new TileRenderer();
export { tileRenderer };
export default TileRenderer;